import * as React from 'react'
import { useDebounce } from 'react-use'
import { Alert } from '@toasttab/buffet-pui-alerts'
import {
  CountryCode,
  getCountries,
  getCountryCode,
  DefaultStrings as CountryDefaultStrings
} from './countries'
import { InputField } from './InputField'
import { Field, fieldsForCountry } from './fields'
import {
  defaultStrings,
  DefaultStrings,
  makeGetStringFunction
} from './strings'
import {
  Address,
  AddressLookup,
  getMapboxSearch,
  Location
} from '@toasttab/buffet-patterns-address-lookup'
import isEqual from 'lodash/isEqual'

export type InternationalAddressProps = {
  /**
   * A testId, defaults to 'international-address'
   */
  testId?: string
  /**
   * A boolean to show the dropdown to select / change country
   */
  showCountryDropdown?: boolean
  /**
   * A callback for when a change to the address form is made
   */
  onChange?: (selection: Address) => void
  /**
   * An initial address
   */
  defaultValue?: Address
  /**
   * The public token for the lookup service (currently mapbox)
   */
  lookupServiceAccessToken?: string
  /**
   * Setting this to true will stop the component from being wrapped in a QueryClientProvider. Use this
   * if you're working on app that already has a react-query QueryClientProvider wrapper.
   */
  hasOwnQueryContext?: boolean
  /**
   * Allows a class name around the whole component for spacing etc
   */
  containerClassName?: string
  /**
   * If parent decides field is invalid, show valid/invalid state on each input
   */
  invalid?: boolean
  /**
   * A boolean to indicate if the fields are required
   *
   * Setting this to false means the address is not required in the form the field is used in
   */
  required?: boolean
  /**
   * Error text displayed above the form elements
   */
  errorText?: string
  /**
   * UI string overrides
   */
  strings?: Partial<DefaultStrings>
  /*
   * Country name overrides, for use when localising
   */
  countryStrings?: Partial<CountryDefaultStrings>
  /**
   * A lat/long proximity location to include in the Mapbox lookup request
   */
  proximity?: Location
  /**
   * Whether to include Points of Interest in the Mapbox lookup request
   */
  includePOI?: boolean
}

export type HandleSetFieldValues = (name: keyof Address, value: string) => void

const getInitialStateName = (defaultValue: Address) => {
  if (defaultValue.stateName?.length) return defaultValue.stateName
  if (defaultValue.stateCode && defaultValue.stateCode !== 'XX')
    return defaultValue.stateCode
  return defaultValue.administrativeArea
}

export const InternationalAddress: React.FC<InternationalAddressProps> = ({
  testId = 'international-address',
  onChange,
  showCountryDropdown,
  defaultValue = {
    country: 'US'
  },
  hasOwnQueryContext,
  containerClassName,
  lookupServiceAccessToken = AddressLookup.publicToken,
  errorText,
  strings,
  invalid,
  required = true,
  countryStrings,
  proximity,
  includePOI
}) => {
  const getString = React.useMemo(
    () =>
      makeGetStringFunction({
        ...defaultStrings,
        ...strings
      }),
    [strings]
  )

  const defaultValues: Address = {
    address1: '',
    address2: '',
    city: '',
    stateCode: '',
    administrativeArea: '',
    zipCode: '',
    country: getCountryCode(defaultValue.country),
    latitude: 0,
    longitude: 0
  }

  const shouldUseCountryCodeForStateCode = (fieldValues: Address) =>
    fieldValues.country !== 'US' && fieldValues.country !== 'CA'

  const supportedCountryCodes = ['US', 'CA', 'GB', 'IE']

  const [selectedCountry, setSelectedCountry] = React.useState<CountryCode>(
    getCountryCode(defaultValues.country)
  )

  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    errorText
  )

  const countryOptions = getCountries({ ...countryStrings })

  const countryFields = fieldsForCountry(
    selectedCountry,
    getString,
    required,
    showCountryDropdown ? countryOptions : undefined
  )
  const [fields, setFields] = React.useState<Field[]>(countryFields)

  const [fieldValues, setFieldValues] = React.useState<Address>({
    ...defaultValues,
    ...defaultValue,
    stateName: getInitialStateName(defaultValue)
  })

  // If the defaultValue prop changes, the component should update with the new values
  // Track previous defaultValue in a ref to avoid infinite re-renders
  const prevDefaultValueRef = React.useRef(defaultValue)

  React.useEffect(() => {
    const prevDefaultValue = prevDefaultValueRef.current
    const hasDefaultValueChanged = !isEqual(prevDefaultValue, defaultValue)

    if (hasDefaultValueChanged) {
      setFieldValues((prev) => ({
        ...prev,
        ...defaultValue,
        stateName: getInitialStateName(defaultValue)
      }))
      prevDefaultValueRef.current = defaultValue
    }
  }, [defaultValue])

  const [address1IsDirty, setAddress1IsDirty] = React.useState(!!invalid)

  const [search, setSearch] = React.useState('')

  const doOnChange = (fieldValues: Address) => {
    // Remove stateName and update stateCode to use name if appropriate
    const updatedFieldValues = {
      ...fieldValues,
      stateCode: shouldUseCountryCodeForStateCode(fieldValues)
        ? supportedCountryCodes.includes(fieldValues.country || '')
          ? fieldValues.country
          : 'XX'
        : fieldValues.stateCode,
      administrativeArea: fieldValues.stateCode
    }
    if (onChange) {
      onChange(updatedFieldValues)
    }
  }

  const doSearch = async () => {
    // Update lat long on field values
    const results = await getMapboxSearch(
      search,
      lookupServiceAccessToken,
      selectedCountry.toLowerCase(),
      undefined,
      proximity,
      includePOI
    )
    const result = results[0]
    // Update lat/long values as the address changes
    if (
      result?.latitude &&
      result?.longitude &&
      (result?.latitude !== fieldValues.latitude ||
        result?.longitude !== fieldValues.longitude)
    ) {
      const updatedFieldValues = {
        ...fieldValues,
        latitude: result?.latitude,
        longitude: result?.longitude
      }
      setFieldValues(updatedFieldValues)
      doOnChange(updatedFieldValues)
    }
  }

  // Handle the lat/long updates with a debounce
  useDebounce(doSearch, 500, [search])

  // Update full address search text for determining lat/long values
  React.useEffect(() => {
    const search = `${fieldValues.address1}${
      fieldValues.address2 ? ',' + fieldValues.address2 : ''
    }${fieldValues.city ? ',' + fieldValues.city : ''}${
      fieldValues.stateCode ? ',' + fieldValues.stateCode : ''
    }${fieldValues.zipCode ? ',' + fieldValues.zipCode : ''}`
    setSearch(search)
  }, [fieldValues])

  React.useEffect(() => setAddress1IsDirty(!!invalid), [invalid])

  React.useEffect(() => {
    const newCountryCode = fieldValues.country
    if (newCountryCode && selectedCountry !== newCountryCode) {
      const newFields = fieldsForCountry(
        newCountryCode as CountryCode,
        getString,
        required,
        showCountryDropdown ? countryOptions : undefined
      )
      const { address1, latitude, longitude } = fieldValues
      const fieldNames = newFields.map((field) => field.name)
      const updatedFieldValues = {
        address1,
        address2: fieldNames.includes('address2') ? fieldValues.address2 : '',
        stateCode: fieldNames.includes('stateCode')
          ? fieldValues.stateCode
          : '',
        administrativeArea: fieldNames.includes('stateCode')
          ? fieldValues.stateCode
          : '',
        stateName: fieldNames.includes('stateName')
          ? fieldValues.stateName
          : '',
        city: fieldNames.includes('city') ? fieldValues.city : '',
        zipCode: fieldNames.includes('zipCode') ? fieldValues.zipCode : '',
        latitude,
        longitude,
        country: newCountryCode
      }
      setSelectedCountry(getCountryCode(newCountryCode))
      setFieldValues(updatedFieldValues)
      setFields(newFields)
      doOnChange(updatedFieldValues)
    }
  }, [fieldValues.country])

  const handleAddress1 = async (address: Address, search?: string) => {
    setAddress1IsDirty(true)
    const updatedFieldValues = {
      ...fieldValues,
      ...address,
      address1: search
    }
    setFieldValues(updatedFieldValues)
    doOnChange(updatedFieldValues)
  }

  const handleSetFieldValues: HandleSetFieldValues = (name, newValue) => {
    if (invalid) {
      setAddress1IsDirty(true)
    }
    const updatedFieldValues = {
      ...fieldValues,
      [name]: newValue
    }
    setFieldValues(updatedFieldValues)
    doOnChange(updatedFieldValues)
  }

  const valueToString = (address: Address | null) => address?.address1 || ''

  const address1Invalid =
    required &&
    address1IsDirty &&
    !(fieldValues['address1'] && fieldValues['address1'].length)

  return (
    <div className={containerClassName} data-testid={testId}>
      {errorMessage && (
        <Alert
          className='w-full'
          onDismiss={() => setErrorMessage(undefined)}
          variant='error'
        >
          {errorMessage}
        </Alert>
      )}
      <div className='flex flex-wrap'>
        <div className='w-full mt-4'>
          <AddressLookup
            name='address1'
            containerClassName='address-lookup'
            label={getString('label.address1')}
            testId={`${testId}-select-address`}
            hasOwnQueryContext={hasOwnQueryContext}
            clearOnBlur={false}
            value={fieldValues}
            countries={selectedCountry.toLowerCase()}
            onChange={handleAddress1}
            lookupServiceAccessToken={lookupServiceAccessToken}
            placeholder={getString('placeholder.address')}
            valueToString={valueToString}
            invalid={address1Invalid}
            errorText={address1Invalid && getString('error.required.address')}
            includeStateName
          />
        </div>

        {fields.map((field) => (
          <InputField
            {...field}
            value={fieldValues[field.name] ? `${fieldValues[field.name]}` : ''}
            key={field.name}
            handleSetFieldValues={handleSetFieldValues}
            testId={`${testId}-${field.name}`}
            validateField={!!invalid}
          />
        ))}
      </div>
    </div>
  )
}
